<!--
  @USE AS:
  import Modal from '@/components/base/Modal'

  components: { Modal }

  <button @click="test = true">MODAL</button>

  <modal
    title="test"
    v-if="test"
    :activate="test"
    :draggable="true"
    @activate="test = $event"
  >
    <template slot="toolbar">
      toolbar
    </template>

    <template slot="body">
      body
    </template>

    <template slot="footer">
      footer
    </template>
  </modal>
-->

<template>
  <transition name="fade" v-if="show">
    <div class="modal" :class="customClass">
      <div
        class="modal--inner"
        v-dragged="onDragged"
        ref="modalInner"
        :style="customStyles ? customStyles : ''"
      > <!-- .modal--inner -->
        <div
          class="modal-header"
          ref="modalHeader"
          :class="'-' + headerClass"
        > <!-- .modal-header -->
          <div class="modal-header--inner">
            <h3
              class="modal__title"
              ref="modalTitle"
              v-if="title"
              v-html="title"
              :style="`width: ${this.title_w}px`"
            >
            </h3>

            <div
              class="modal__toolbar"
              ref="modalToolbar"
              v-if="hasToolbar"
              :style="`width: ${this.toolbar_w}px`"
            >
              <slot name="toolbar"></slot>
            </div>

            <div class="modal__buttons" ref="modalButtons">
              <button
                v-if="isChatbox"
                class="btn btn--minimize"
                @click="closeModal()"
              >
                <i class="fas fa-window-minimize"></i>
              </button>

              <button
                class="btn--popup-close"
                @click="closeModal()"
              >
                <i class="ico--popup-close"></i>
              </button>
            </div>
          </div>
        </div> <!-- // .modal-header -->

        <div
          class="modal-body"
          ref="modalBody"
          :style="setModalBodyHeight"
          v-if="hasBody"
        > <!-- .modal-body -->
          <div class="modal-body--inner">
            <slot name="body"></slot>
          </div>
        </div> <!-- // .modal-body -->

        <div
          class="modal-footer"
          ref="modalFooter"
          v-if="hasFooter"
        >
          <div class="modal-footer--inner">
            <slot name="footer"></slot>
          </div>
        </div>
      </div> <!-- // .modal--inner -->
    </div>
  </transition>
</template>

<script>
import Vue from 'vue'

//= jquery
import $ from 'jquery'

//= mixins
import { detectDevice } from '@/assets/js/mixins/base/DetectDevice'

import VDragged from 'v-dragged'
Vue.use(VDragged)

export default {
  name: 'modal',

  mixins: [ detectDevice ],

  props: {
    activate: Boolean,
    title: String,
    customId: String,
    customClass: String,
    customStyles: [Object, Array],
    logs: [Object, Array],

    draggable: {
      type: [String, Boolean],
      default: false
    },

    bodyOverflow: {
      type: Boolean,
      default: false
    },

    isChatbox: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      isDraggable: false,
      show: false,

      headerClass: '',
      title_w: '',
      toolbar_w: 'auto',

      modalBody: 'auto'
    }
  },

  computed: {
    hasToolbar () {
      return this.$slots['toolbar']
    },

    hasBody () {
      return this.$slots['body']
    },

    hasFooter () {
      return this.$slots['footer']
    },

    setModalBodyHeight () {
      return { 'max-height': this.modalBody + 'px' }
    } // setModalBodyHeight
  },

  watch: {
    activate () {
      if (this.activate) {
        this.modalActive()
      }

      if (!this.activate) {
        this.closeModal()
      }
    } // activate
  },

  mounted () {
    this.$nextTick(function () {
      window.addEventListener('resize', this.getDraggable)
      this.getDraggable()

      window.addEventListener('resize', this.setWidth)
      this.setWidth()

      window.addEventListener('resize', this.getModalBodyHeight)
      this.getModalBodyHeight()

      this.setHeaderClass()
    })

    if (this.activate) {
      this.modalActive()
    }
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.getDraggable)
    window.removeEventListener('resize', this.setWidth)
    window.removeEventListener('resize', this.getModalBodyHeight)
  },

  methods: {
    setHeaderClass () {
      var title = $('.modal__title').outerWidth()
      var toolbar = $('.modal__toolbar').outerWidth()
      var buttons = $('.modal__buttons').outerWidth()

      if (typeof title !== 'undefined' && typeof toolbar !== 'undefined' && typeof buttons !== 'undefined') {
        this.headerClass = 'title-button-toolbar'
      }

      if (typeof title !== 'undefined' && typeof toolbar === 'undefined' && typeof buttons !== 'undefined') {
        this.headerClass = 'title-button'
      }

      if (typeof title === 'undefined' && typeof toolbar !== 'undefined' && typeof buttons !== 'undefined') {
        this.headerClass = 'button-toolbar'
      }

      if (typeof title === 'undefined' && typeof toolbar === 'undefined' && typeof buttons !== 'undefined') {
        this.headerClass = 'button'
      }
    },

    getModalBodyHeight () {
      this.$nextTick(() => {
        let modalHeader = this.$refs.modalHeader.offsetHeight
        let browserHeight = this.device_height
        let padding = 65

        if (this.device_height > 799) {
          if (this.device_width > 991) {
            if (this.$refs.modalFooter) {
              let modalFooter = this.$refs.modalFooter.offsetHeight
              this.modalBody = browserHeight - modalHeader - modalFooter - padding
            } else {
              this.modalBody = browserHeight - modalHeader - padding
            }
          } // > 991
        } // > 799
      })
    }, // setModalBodyHeight

    setWidth () {
      this.$nextTick(() => {
        var modalWidth = this.$refs.modalInner
        var title = this.$refs.modalTitle
        var toolbar = this.$refs.modalToolbar
        var buttons = this.$refs.modalButtons

        if (title && !buttons && !toolbar) {
          this.title_w = modalWidth.offsetWidth
        }

        if (title && buttons && !toolbar) {
          this.title_w = modalWidth.offsetWidth - buttons.offsetWidth - 4
        }

        if (title && buttons && toolbar) {
          this.title_w = modalWidth.offsetWidth - buttons.offsetWidth - toolbar.offsetWidth - (4 * 2)
        }

        if (!title && !toolbar && buttons) {
          this.title_w = ''
        }

        if (!title && toolbar && buttons) {
          this.toolbar_w = modalWidth.offsetWidth - buttons.offsetWidth - 4
        }
      })
    }, // setWidth

    getDraggable () {
      var cm = this.$refs.modalBody

      this.setDraggable()

      cm.addEventListener('mouseover', () => { this.isDraggable = false })
      cm.addEventListener('mouseout', () => { this.setDraggable() })
    }, // getDraggable

    setDraggable () {
      var drag = this.draggable

      if (drag === false) {
        this.isDraggable = false
      } else {
        if (this.device_width > 991) {
          this.isDraggable = true
        } else {
          $('.modal--inner').removeAttr('style')
          this.isDraggable = false
        }
      }
    }, // setDraggable

    closeModal () {
      this.show = false
      this.$emit('activate', this.show)
      $('body').removeAttr('style')
      $('body').removeClass('modal--open')
    }, // closeModal

    closeChatbox () {
      this.closeModal()
      this.$emit('closeChatbox', '')
    }, // closeChatbox

    modalActive () {
      this.show = true
      $('body').addClass('modal--open')

      if (this.bodyOverflow === false) {
        $('body').css({ 'overflow': 'hidden' })
      }
    }, // modalActive

    onDragged ({ el, deltaX, deltaY, offsetX, offsetY, clientX, clientY, first, last }) {
      if (this.isDraggable) {
        var l = +window.getComputedStyle(el)['left'].slice(0, -2) || 0
        var t = +window.getComputedStyle(el)['top'].slice(0, -2) || 0
        el.style.left = l + deltaX + 'px'
        el.style.top = t + deltaY + 'px'
      }
    } // onDragged
  }
}
</script>
